import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { PriorityServiceAreaPage } from "@/components/PriorityServiceAreaPage";
import { seoHead, serviceSchema, localBusinessSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";

const FAQS = [
  { q: "Do you fit tyres while I wait in Poplar?", a: "Yes — most tyre fits take 30–45 minutes per pair. Pop in for a coffee or use the wait time to walk into Poplar." },
  { q: "What tyre brands do you fit?", a: "Premium (Michelin, Continental, Bridgestone), mid-range (Avon, Falken, Hankook) and budget options. We'll quote 2–3 choices so you can decide on price vs longevity." },
  { q: "Can you fit a single tyre or do I need a pair?", a: "Legally a single tyre is fine, but for handling we recommend pairs on the same axle when tread depths differ significantly. We'll advise honestly." },
  { q: "Do you offer puncture repairs?", a: "Yes — where the puncture is in the central tread area (not the sidewall) and the tyre is otherwise sound. Cheaper and quicker than replacing." },
];

export const Route = createFileRoute("/poplar/tyres")({
  head: () => seoHead({
    path: "/poplar/tyres",
    title: "Tyres in Poplar, E14 — Fitting, Balancing & Puncture Repair | Woodseer Autos",
    description: `Tyre fitting, balancing and puncture repair in Poplar, E14. Premium and budget brands. Honest advice. Call ${NAP.phone}.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Poplar", path: "/areas/poplar" },
      { name: "Tyres", path: "/poplar/tyres" },
    ],
    jsonLd: [
      localBusinessSchema({ serviceCoverage: ["Poplar"] }),
      serviceSchema({
        name: "Tyre Fitting in Poplar",
        serviceType: "Tyre fitting and repair",
        description: "Tyre fitting, balancing and puncture repair for cars and light vans in Poplar, E14. Premium, mid-range and budget brands available.",
        areaServed: "Poplar, London",
        url: "https://woodseerautos.co.uk/poplar/tyres",
      }),
      faqSchema(FAQS) as Record<string, unknown>,
    ],
  }),
  component: () => (
    <PriorityServiceAreaPage
      areaSlug="poplar"
      areaName="Poplar"
      serviceSlug="tyres"
      serviceName="Tyres"
      parentServiceSlug="mechanical-repairs"
      hero="Tyre fitting, balancing, puncture repair and TPMS valves for Poplar drivers. We stock common sizes and can usually source anything else within a day."
      localBenefit="Most tyre fits done in 30–45 minutes — walk to Poplar DLR while you wait"
      whatsIncluded={[
        "New tyres fitted and balanced",
        "Old tyre disposal (compliant)",
        "TPMS valve fitted where applicable",
        "Puncture repair where safe and legal",
        "Tread depth and wear inspection",
        "Pressure set to manufacturer spec",
      ]}
      commonIssues={[
        "Low tread (under 3mm — start planning)",
        "Sidewall damage from kerbing",
        "Uneven wear (alignment or pressure issue)",
        "Slow puncture (often a nail in the tread)",
        "TPMS warning light staying on",
        "Cracking on older tyres",
      ]}
      process={[
        { step: "Quick check", desc: "We confirm size, load and speed rating and quote 2–3 brand options." },
        { step: "Fitting & balancing", desc: "On modern machines that protect the wheel — no kerbing or paint scratches." },
        { step: "Pressures & TPMS", desc: "Set to manufacturer spec and TPMS reset where needed." },
        { step: "Drive off", desc: "We'll suggest re-checking torque after 50 miles to be safe." },
      ]}
      whyUs={[
        "Premium, mid and budget brands quoted side-by-side",
        "Modern fitting machines — no wheel damage",
        "TPMS valves and reset included where applicable",
        "Puncture repair option to save you money",
      ]}
      faqs={FAQS}
    />
  ),
});
